<template>
  <header class="autosyncx-header">
    <!-- Top Banner with Skyline Background -->
    <div class="header-skyline">
      <div class="header-skyline-content">
        <!-- Hamburger Menu (Mobile Only) - Left of Logo -->
        <div class="hamburger-menu" @click="toggleMobileMenu">
          <v-icon class="hamburger-icon material-icons-round">{{ mobileMenuOpen ? 'close' : 'menu' }}</v-icon>
        </div>

        <!-- Center: AUTOSYNCX Logo -->
        <div class="header-logo" @click="goToInventory">
          <img src="@/assets/img/inventory-logo.png" alt="Herrera Luxury Auto Sales Logo" class="logo-image" />
        </div>

        <!-- Right: Back to Inventory -->
        <div class="header-actions">
          <v-btn
            text
            small
            @click="goToInventory"
            class="back-btn"
          >
            <v-icon small class="material-icons-round mr-1">arrow_back</v-icon>
            Back to Inventory
          </v-btn>
        </div>
      </div>

      <!-- Mobile Menu Dropdown -->
      <v-expand-transition>
        <div v-if="mobileMenuOpen" class="mobile-menu-dropdown">
          <div class="mobile-menu-item" @click="goToInventory">
            <i class="material-icons-round menu-item-icon">inventory_2</i>
            <span>Back to Inventory</span>
          </div>
          <div class="mobile-menu-item" @click="goToCard">
            <i class="material-icons-round menu-item-icon">contact_page</i>
            <span>My Card</span>
          </div>
          <div class="mobile-menu-item" @click="toggleDarkMode">
            <i class="material-icons-round menu-item-icon">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</i>
            <span>{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
          </div>
          <div class="mobile-menu-item" @click="callDealer">
            <i class="material-icons-round menu-item-icon">call</i>
            <span>Call Us</span>
          </div>
        </div>
      </v-expand-transition>
    </div>

    <!-- Navigation Bar -->
    <div class="header-nav">
      <div class="header-nav-content">
        <div class="nav-tabs">
          <div class="nav-tab" @click="goToInventory">
            <v-icon small>mdi-view-grid</v-icon>
            <span>INVENTORY</span>
          </div>
          <div class="nav-tab active">
            <v-icon small>mdi-car</v-icon>
            <span>VEHICLE DETAILS</span>
          </div>
        </div>

        <v-spacer></v-spacer>

        <!-- Search Box -->
        <div class="nav-search">
          <v-icon small color="grey">mdi-magnify</v-icon>
          <input
            type="text"
            v-model="searchTerm"
            placeholder="Search..."
            class="search-input"
          />
        </div>

        <!-- Dark/Light Mode Toggle -->
        <div class="theme-toggle">
          <v-btn icon small @click="toggleDarkMode" class="theme-toggle-btn">
            <v-icon small class="material-icons-round">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: 'PublicHeader',
  data() {
    return {
      drawer: false,
      mobileMenuOpen: false,
      isDarkMode: false,
      searchTerm: ''
    }
  },
  mounted() {
    // Load dark mode preference from localStorage
    const savedDarkMode = localStorage.getItem('isDarkMode');
    this.isDarkMode = savedDarkMode === 'true';
    this.applyDarkMode();
  },
  methods: {
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },

    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem('isDarkMode', this.isDarkMode);
      this.applyDarkMode();
      this.mobileMenuOpen = false;

      // Dispatch custom event so parent components can react
      window.dispatchEvent(new CustomEvent('darkModeChanged', {
        detail: { isDarkMode: this.isDarkMode }
      }));
    },

    applyDarkMode() {
      // Apply dark mode class to document body
      if (this.isDarkMode) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
    },

    goHome() {
      this.$router.push('/')
      this.mobileMenuOpen = false
    },

    goToCard() {
      this.$router.push('/card')
      this.mobileMenuOpen = false
    },

    goToInventory() {
      this.$router.push('/inventory')
      this.mobileMenuOpen = false
    },

    callDealer() {
      // Get company phone from localStorage or use default
      const company = JSON.parse(localStorage.getItem('company') || '{}');
      const phone = company.phone_m || company.phone || '';
      if (phone) {
        window.location.href = `tel:${phone}`;
      }
      this.mobileMenuOpen = false
    }
  }
}
</script>

<style scoped>
/* AutoSyncX Header - Matching CardInventory Theme */
.autosyncx-header {
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}

/* Skyline Banner */
.header-skyline {
  background: linear-gradient(to bottom, #d8dce3 0%, #c5c9d1 100%);
  background-image: url('~@/assets/img/skyline-bg.png');
  background-size: cover;
  background-position: center bottom;
  padding: 20px 30px;
  position: relative;
}

.header-skyline-content {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
}

/* Hamburger Menu */
.hamburger-menu {
  display: none;
  cursor: pointer;
  padding: 0px;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: absolute;
  left: 0px;
  top: 50%;
  transform: translateY(-50%);
}

.hamburger-menu:hover {
  background: rgba(0, 0, 0, 0.1);
}

.hamburger-icon {
  color: #5a5a7a !important;
  font-size: 28px !important;
}

/* Logo */
.header-logo {
  text-align: center;
  cursor: pointer;
}

.logo-image {
  height: 50px;
  width: auto;
  object-fit: contain;
}

/* Header Actions */
.header-actions {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 0.85rem !important;
  color: #4a5568 !important;
}

/* Mobile Menu Dropdown */
.mobile-menu-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 245, 250, 0.98) 100%);
  backdrop-filter: blur(20px);
  padding: 10px 0;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  color: #374151;
  cursor: pointer;
  transition: all 0.3s ease;
}

.mobile-menu-item:hover {
  background: rgba(59, 130, 246, 0.1);
}

.menu-item-icon {
  color: #6366f1 !important;
  font-size: 22px !important;
}

.mobile-menu-item span {
  font-size: 0.95rem;
  font-weight: 500;
}

/* Navigation Bar */
.header-nav {
  background: #3d4a5c;
  padding: 0 30px;
  min-height: 48px;
}

.header-nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 48px;
}

.nav-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 2px solid transparent;
}

.nav-tab:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.1);
}

.nav-tab.active {
  color: #ffffff;
  border-bottom-color: #7c5cff;
  background: rgba(124, 92, 255, 0.15);
}

.nav-tab .v-icon {
  font-size: 18px !important;
  color: inherit !important;
}

/* Search Box */
.nav-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 6px 14px;
  min-width: 200px;
  transition: all 0.3s ease;
}

.nav-search:focus-within {
  background: rgba(255, 255, 255, 0.25);
}

.nav-search .v-icon {
  color: rgba(255, 255, 255, 0.7) !important;
}

.search-input {
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 0.85rem;
  width: 100%;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

/* Theme Toggle */
.theme-toggle {
  margin-left: 12px;
}

.theme-toggle-btn {
  color: rgba(255, 255, 255, 0.9) !important;
  transition: all 0.3s ease;
}

.theme-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.15) !important;
}

.theme-toggle-btn .v-icon {
  color: rgba(255, 255, 255, 0.9) !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-skyline {
    padding: 15px 20px;
  }

  .hamburger-menu {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .header-actions {
    display: none;
  }

  .header-nav {
    padding: 0 15px;
  }

  .nav-tabs {
    display: none;
  }

  .nav-search {
    flex: 1;
    min-width: unset;
  }
}

@media (max-width: 480px) {
  .logo-image {
    height: 40px;
  }

  .nav-search {
    display: none;
  }
}
</style>
